import { FC, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { createColumnHelper, ColumnDef } from '@tanstack/react-table';
import {
  Accordion,
  AccordionItem,
  DataTableSkeleton,
  InlineNotification,
} from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { StatsBar } from 'components/stats-bar';
import { DataTable } from 'components/data-table';
import { orphanedGeometriesLoadableAtom } from './atoms';
import type { OrphanedGeometry } from './types';
import styles from './page.module.scss';

const TABLE_CONFIG = {
  sorting: true,
  globalSearch: true,
  columnFilters: false,
  columnResizing: true,
  columnReordering: true,
  columnPinning: true,
  columnVisibility: true,
} as const;

const columnHelper = createColumnHelper<OrphanedGeometry>();

const baseColumns = [
  columnHelper.accessor('subtractId', {
    id: 'subtractId',
    header: 'Subtract ID',
    size: 200,
  }),
  columnHelper.accessor('subtractName', {
    id: 'subtractName',
    header: 'Subtract Name',
    size: 200,
    cell: (info) => info.getValue() ?? '--',
  }),
  columnHelper.accessor('geometryCreatedAt', {
    id: 'geometryCreatedAt',
    header: 'Geometry Created',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
  columnHelper.accessor('geometryUpdatedAt', {
    id: 'geometryUpdatedAt',
    header: 'Geometry Updated',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

const softDeletedColumns = [
  ...baseColumns,
  columnHelper.accessor('subtractDeletedAt', {
    id: 'subtractDeletedAt',
    header: 'Subtract Deleted At',
    size: 200,
    cell: (info) => {
      const value = info.getValue();
      return value ? new Date(value).toLocaleString() : '--';
    },
  }),
];

const noSubtractColumns = baseColumns;

export const OrphanedSubtractGeometriesPage: FC = () => {
  const loadable = useAtomValue(orphanedGeometriesLoadableAtom);

  const data = useMemo(() => {
    return loadable.state === 'hasData' ? loadable.data : null;
  }, [loadable]);

  const isLoading = loadable.state === 'loading';
  const error =
    loadable.state === 'hasError'
      ? loadable.error instanceof Error
        ? loadable.error.message
        : 'Failed to fetch data'
      : null;

  return (
    <>
      <PageHeader
        breadcrumbs={[['/admin/dashboard', 'Admin']]}
        title="Orphaned Subtract Geometries"
        actions={null}
      />
      <div className={styles.sectionHeader}>
        <div className={styles.sectionDescription}>
          Subtract geometries where the associated subtract has been deleted or
          does not exist. These geometries are sent by GIS but have no active
          subtract to attach to. The GIS team should review and either delete
          the geometry or re-create the subtract in Salesforce.
        </div>
      </div>

      {isLoading && <DataTableSkeleton columnCount={5} rowCount={10} />}
      {error && (
        <InlineNotification
          kind="error"
          title="Error"
          subtitle={error}
          hideCloseButton
        />
      )}

      {!isLoading && !error && data && (
        <>
          <StatsBar
            stats={[
              {
                label: 'Deleted Subtracts',
                value: data.stats.softDeleted,
                tagType: data.stats.softDeleted > 0 ? 'red' : 'green',
              },
              {
                label: 'No Subtract Record',
                value: data.stats.noSubtract,
                tagType: data.stats.noSubtract > 0 ? 'red' : 'green',
              },
            ]}
          />

          <Accordion>
            <AccordionItem
              title={`Geometries for Deleted Subtracts (${data.stats.softDeleted})`}
              open={data.stats.softDeleted > 0}
            >
              <div className={styles.accordionDescription}>
                The subtract exists in Salesforce but is marked as deleted
                (IsDeleted=true). The geometry is still in our system. Either
                the subtract should be undeleted in Salesforce, or the geometry
                should be removed from the GIS source.
              </div>
              <DataTable
                data={data.softDeleted}
                columns={softDeletedColumns as ColumnDef<OrphanedGeometry>[]}
                getRowId={(row) => `${row.id}`}
                isLoading={false}
                persistenceKey="admin-orphaned-sg-soft-deleted"
                config={TABLE_CONFIG}
              />
            </AccordionItem>

            <AccordionItem
              title={`Geometries with No Subtract Record (${data.stats.noSubtract})`}
              open={data.stats.noSubtract > 0}
            >
              <div className={styles.accordionDescription}>
                No subtract record exists in our database for these geometries.
                The subtract was either never ingested from Salesforce or was
                hard-deleted. The GIS team should either create the subtract in
                Salesforce or stop sending the geometry.
              </div>
              <DataTable
                data={data.noSubtract}
                columns={noSubtractColumns as ColumnDef<OrphanedGeometry>[]}
                getRowId={(row) => `${row.id}`}
                isLoading={false}
                persistenceKey="admin-orphaned-sg-no-subtract"
                config={TABLE_CONFIG}
              />
            </AccordionItem>
          </Accordion>
        </>
      )}
    </>
  );
};
